import FadeIn from '@/components/FadeIn/FadeIn';
import { FEEDBACK_DATA } from '@/constants/home';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { FC, HTMLAttributes, ReactNode, useState } from 'react';
import { getImageUrl } from '@/lib/cdn';

type IB2BLeadersProps = object;

interface IFeedbackCard extends HTMLAttributes<HTMLDivElement> {
  onNext?: () => void;
  onBack?: () => void;
  feedback: {
    name: string;
    title: string;
    imageUrl: string;
    testimonial: string;
    logoUrl: string;
    linkedinUrl: string;
    stat1Value: string; // e.g., "80%"
    stat1Label: string; // e.g., "Increase in Sales Efficiency"
    stat2Value: string; // e.g., "90%"
    stat2Label: string; // e.g., "Uplift in Website Conversion"
  };
  CustomContentComponent?: ReactNode;
  hideButtons?: boolean;
}

export const FeedbackCard = ({
  onNext,
  onBack,
  feedback: activeFeedback,
  className,
  CustomContentComponent,
  hideButtons,
}: IFeedbackCard) => {
  return (
    <div
      className={clsx(
        'feedback-card py-4 px-4 xl:py-8 xl:px-6 rounded-[27px] bg-[#1D4ED8] flex flex-col',
        className,
      )}
    >
      <div className='flex justify-between pb-4 xl:pb-10'>
        <Image
          className='w-auto h-8 xl:h-10 object-contain'
          src={activeFeedback.logoUrl}
          alt=''
          width={200}
          height={40}
        />
        {!hideButtons && (
          <div className='flex items-center gap-3'>
            <div
              className='p-1.5 rounded-lg bg-white/15 text-white cursor-pointer'
              onClick={onBack}
            >
              <ChevronLeft size={22} />
            </div>
            <div
              className='p-1.5 rounded-lg bg-white/15 text-white cursor-pointer'
              onClick={onNext}
            >
              <ChevronRight size={22} />
            </div>
          </div>
        )}
      </div>

      {CustomContentComponent ? (
        CustomContentComponent
      ) : (
        <>
          <div className='flex sm:flex-row flex-col sm:items-center gap-4 xl:gap-7.5'>
            <p className='flex-1 text-white flex flex-col xl:gap-2'>
              <span className='text-sectionDesc xl:text-[1.875rem] font-semibold'>
                {activeFeedback.stat1Value}
              </span>
              <span className='text-2xl xl:text-[1.25rem] font-light'>
                {activeFeedback.stat1Label}
              </span>
            </p>
            <p className='flex-1 text-white flex flex-col xl:gap-2'>
              <span className='text-sectionDesc xl:text-[1.875rem] font-semibold'>
                {activeFeedback.stat2Value}
              </span>
              <span className='text-2xl xl:text-[1.25rem] font-light'>
                {activeFeedback.stat2Label}
              </span>
            </p>
          </div>

          <p className='text-xl xl:text-2xl text-white font-normal mt-3 xl:mt-6 flex-1'>
            "{activeFeedback.testimonial}"
          </p>
        </>
      )}

      <div className='items-center flex gap-2.5 xl:gap-4 mt-4 xl:mt-6'>
        <Image
          className='self-center align-middle inline-block w-12 h-12 xl:w-16 xl:h-16 max-w-full rounded-full'
          src={activeFeedback.imageUrl}
          alt={activeFeedback.name}
          width={48}
          height={48}
        />
        <div>
          <a
            href={activeFeedback.linkedinUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='hover:opacity-80 transition-opacity'
          >
            <div className='text-xl xl:text-2xl font-medium flex items-center gap-1 xl:gap-2 text-white'>
              {activeFeedback.name}
              <svg className='w-5 h-5 fill-current' viewBox='0 0 24 24'>
                <path d='M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z' />
              </svg>
            </div>
          </a>
          <div className='text-white/70 text-base md:text-sm inline-block mt-1'>
            {activeFeedback.title}
          </div>
        </div>
      </div>
    </div>
  );
};

const B2BLeaders: FC<IB2BLeadersProps> = (props) => {
  const [activeFeedback, setActiveFeedback] = useState(FEEDBACK_DATA[0]);

  const handleNextFeedback = () => {
    const currentIndex = FEEDBACK_DATA.indexOf(activeFeedback);
    const nextIndex = (currentIndex + 1) % FEEDBACK_DATA.length;
    setActiveFeedback(FEEDBACK_DATA[nextIndex]);
  };

  const handleBackFeedback = () => {
    const currentIndex = FEEDBACK_DATA.indexOf(activeFeedback);
    const prevIndex = (currentIndex - 1 + FEEDBACK_DATA.length) % FEEDBACK_DATA.length;
    setActiveFeedback(FEEDBACK_DATA[prevIndex]);
  };

  return (
    <section className='section__b2b-leaders pt-[66px]'>
      <div className='container-fluid px-4 md:px-7.5'>
        <div className='bg-[#F2F6FF] rounded-3xl px-[34px] py-10'>
          <FadeIn variant='down' delay={0.3}>
            <p className='text-3xl sm:text-sectionTitleBase xl:text-sectionTitle font-medium pb-2 xl:pb-4 text-center'>
              How B2B leaders 10x their pipeline
            </p>
            <p className='text-xl sm:text-3xl xl:text-sectionDesc text-black-200 text-center'>
              We deliver real revenue, not just leads
            </p>
          </FadeIn>
          <div className='relative'>
            <FadeIn variant='up' delay={0.3}>
              <Image
                className='w-full h-auto mt-7 rounded-[20px] mx-auto'
                src={getImageUrl('revenue/b2b-leads.webp')}
                alt=''
                width={9999}
                height={9999}
              />
            </FadeIn>

            <FeedbackCard
              className='max-w-[617px] absolute top-1/2 -translate-y-1/2 right-13 min-h-[350px] xl:min-h-[470px] flex-col hidden min-[930px]:flex'
              onBack={handleBackFeedback}
              onNext={handleNextFeedback}
              feedback={activeFeedback}
            />
          </div>
          {/* Show all feedbacks on mobile */}
          <FadeIn
            variant='up'
            delay={0.35}
            className='w-full grid min-[930px]:hidden grid-cols-1 tablet:grid-cols-2 gap-4 pt-4'
          >
            {FEEDBACK_DATA.map((item, index) => (
              <FeedbackCard
                className='sticky top-[100px] shadow-drop'
                key={index}
                feedback={item}
                hideButtons
              />
            ))}
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

